import { useMemo } from 'react';
import useSWR from 'swr';
import { fetcher } from '../services/fetcher';
import { isActiveLookupItem } from '../lib/isActiveLookup';

/** Normalize API response to array (backend may return array or { data: [] } / { records: [] }) */
function toOptionsList(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: any[] }).data)) {
    return (raw as { data: any[] }).data;
  }
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: any[] }).records)) {
    return (raw as { records: any[] }).records;
  }
  return [];
}

/** Lookup options for selects — inactive rows are omitted. */
function toActiveOptionsList(raw: unknown): any[] {
  return toOptionsList(raw).filter(isActiveLookupItem);
}

/**
 * Custom hook to fetch options from multiple API endpoints
 * Uses a fixed number of hooks to satisfy React's rules
 * @param endpoints - Record of fieldKey -> { endpoint, labelField?, valueField? }
 * @returns Record of fieldKey -> options array (raw API items; value/label mapping done in field renderer)
 */
export const useApiOptions = (
  endpoints: Record<string, { endpoint: string; labelField?: string; valueField?: string }>
): Record<string, any[]> => {
  // Create a stable, sorted array of endpoint entries
  const endpointEntries = useMemo(() => {
    return Object.entries(endpoints).sort(([a], [b]) => a.localeCompare(b));
  }, [endpoints]);

  // Call exactly 10 hooks unconditionally (can be increased if needed)
  // This satisfies React's rules of hooks
  const hook1 = useSWR<any>(
    endpointEntries[0]?.[1]?.endpoint ? `api-options-${endpointEntries[0][0]}-${endpointEntries[0][1].endpoint}` : null,
    () => fetcher(endpointEntries[0][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook2 = useSWR<any[]>(
    endpointEntries[1]?.[1]?.endpoint ? `api-options-${endpointEntries[1][0]}-${endpointEntries[1][1].endpoint}` : null,
    () => fetcher(endpointEntries[1][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook3 = useSWR<any[]>(
    endpointEntries[2]?.[1]?.endpoint ? `api-options-${endpointEntries[2][0]}-${endpointEntries[2][1].endpoint}` : null,
    () => fetcher(endpointEntries[2][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook4 = useSWR<any[]>(
    endpointEntries[3]?.[1]?.endpoint ? `api-options-${endpointEntries[3][0]}-${endpointEntries[3][1].endpoint}` : null,
    () => fetcher(endpointEntries[3][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook5 = useSWR<any[]>(
    endpointEntries[4]?.[1]?.endpoint ? `api-options-${endpointEntries[4][0]}-${endpointEntries[4][1].endpoint}` : null,
    () => fetcher(endpointEntries[4][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook6 = useSWR<any[]>(
    endpointEntries[5]?.[1]?.endpoint ? `api-options-${endpointEntries[5][0]}-${endpointEntries[5][1].endpoint}` : null,
    () => fetcher(endpointEntries[5][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook7 = useSWR<any[]>(
    endpointEntries[6]?.[1]?.endpoint ? `api-options-${endpointEntries[6][0]}-${endpointEntries[6][1].endpoint}` : null,
    () => fetcher(endpointEntries[6][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook8 = useSWR<any[]>(
    endpointEntries[7]?.[1]?.endpoint ? `api-options-${endpointEntries[7][0]}-${endpointEntries[7][1].endpoint}` : null,
    () => fetcher(endpointEntries[7][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook9 = useSWR<any[]>(
    endpointEntries[8]?.[1]?.endpoint ? `api-options-${endpointEntries[8][0]}-${endpointEntries[8][1].endpoint}` : null,
    () => fetcher(endpointEntries[8][1].endpoint),
    { revalidateOnFocus: false }
  );
  const hook10 = useSWR<any[]>(
    endpointEntries[9]?.[1]?.endpoint ? `api-options-${endpointEntries[9][0]}-${endpointEntries[9][1].endpoint}` : null,
    () => fetcher(endpointEntries[9][1].endpoint),
    { revalidateOnFocus: false }
  );

  // Map hook results back to field keys
  const hooks = [hook1, hook2, hook3, hook4, hook5, hook6, hook7, hook8, hook9, hook10];
  const apiOptionsData: Record<string, any[]> = {};

  hooks.forEach((hook, index) => {
    if (endpointEntries[index]) {
      const [fieldKey] = endpointEntries[index];
      apiOptionsData[fieldKey] = toActiveOptionsList(hook.data);
    }
  });

  return apiOptionsData;
};
